import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Link as RouterLink, useHistory } from 'react-router-dom';
import {
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Menu,
} from '@material-ui/core';
import { IconBaseProps } from 'react-icons';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';

import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/auth';

import { useStyles } from './styles';
import { faLogEvent } from '../../../services/firebase/analytics';

interface Menu {
  href: string;
  title: string;
  disabled: boolean;
}

interface INavItemProps {
  className?: string;
  href: string;
  icon?: React.ComponentType<IconBaseProps>;
  title: string;
  disabled?: boolean;
  menu: Menu[];
  index: number;
  totalItems: number;
  drawerOpen: boolean;
  onCloseDrawer: any;
}

const NavItemMobile: React.FC<INavItemProps> = ({
  href,
  icon: Icon,
  title,
  disabled,
  menu,
  onCloseDrawer,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { signOut } = useAuth();
  const listRef = useRef(null);
  const [isItemFocused, setIsItemFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const titleStr: any = title;

  const handleSelectItem = (): void => {
    setOpen(!open);
  };

  const handleClickMenuItem = (event: any, buttonName: string): void => {
    const buttonNameStr: any = buttonName;
    const translatedButtonName = t(buttonNameStr);
    if (
      translatedButtonName.toLowerCase() ===
      t('components:nav.logout').toLowerCase()
    ) {
      signOut();
      faLogEvent('LOGOUT');
    } else if (
      translatedButtonName.toLowerCase() ===
      t('routes:central-de-ajuda').toLowerCase()
    ) {
      history.push('/dashboard/central-de-ajuda');
    } else {
      handleSelectItem();
    }
  };

  const handleClickSubmenuItem = useCallback(() => {
    onCloseDrawer();
    setOpen(false);
  }, [onCloseDrawer]);

  useEffect(() => {
    const path = location.pathname.split('/');
    const hrefComp = href.split('/');

    if (path[2] === hrefComp[2]) {
      setIsItemFocused(true);
    } else {
      setIsItemFocused(false);
    }
  }, [href, location.pathname]);

  return (
    <>
      <ListItem
        ref={listRef}
        className={classes.item}
        button
        key={title}
        disabled={disabled}
        selected={isItemFocused}
        onClick={event => handleClickMenuItem(event, t(titleStr))}
      >
        <Button
          className={classes.button}
          disableFocusRipple
          disableTouchRipple
          disableRipple
          focusRipple={false}
        >
          <ListItemIcon>
            {Icon && (
              <Icon
                className={isItemFocused ? classes.iconSelected : classes.icon}
                size="20"
              />
            )}
          </ListItemIcon>
          <ListItemText
            primary={t(titleStr)}
            className={isItemFocused ? classes.titleSelected : classes.title}
          />
        </Button>
        {menu.length > 0 && open && (
          <FaChevronUp className={classes.openSubMenuIcon} size={18} />
        )}
        {menu.length > 0 && !open && (
          <FaChevronDown className={classes.openSubMenuIcon} size={18} />
        )}
      </ListItem>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {menu.map(item => {
            const itemTitleStr: any = item.title;
            return (
              <ListItem
                key={item.href}
                disabled={item.disabled}
                className={classes.item}
              >
                <Button
                  className={classes.button}
                  disabled={item.disabled}
                  component={RouterLink}
                  to={item.href}
                  onClick={handleClickSubmenuItem}
                >
                  <ListItemText
                    disableTypography
                    className={classes.submenuTitle}
                    primary={t(itemTitleStr)}
                  />
                </Button>
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </>
  );
};

export default NavItemMobile;
